// pkg105a — My Account (D-105a-1): the admin's own identity card + the "Signed-in devices" card over the pkg105 session registry (GET/DELETE /api/auth/sessions, self-scoped). Revoke copy is deliberately honest: killing the refresh token signs the device out at its next renewal (≤8h), not instantly (D-105a-5) — delivered 2026-07-25
import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminApi } from '../utils/adminApi';

const LABEL = { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };
const VALUE = { fontWeight: 500 };

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function kindBadge(kind, platform) {
  const mobile = kind === 'mobile';
  const label = mobile ? (platform === 'android' ? 'Android' : 'iOS') : 'Web';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
      fontFamily: 'Arial, sans-serif', fontWeight: 600,
      background: mobile ? '#0F1838' : '#F5F1E8',
      color: mobile ? '#F5F1E8' : '#0F1838',
      border: mobile ? 'none' : '1px solid #ddd',
    }}>
      {label}
    </span>
  );
}

export default function AdminMyAccountPage() {
  const { adminUser } = useAdminAuth();
  const [sessions, setSessions] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revoking, setRevoking] = useState(null); // session id mid-revoke

  async function load() {
    try {
      const data = await adminApi.listSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.message);
      setSessions([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRevoke(s) {
    const what = s.kind === 'mobile' ? (s.deviceName || 'this mobile device') : 'this web session';
    if (!window.confirm(`Sign out ${what}? It will take full effect when that session next renews (within 8 hours).`)) return;
    setRevoking(s.id); setError(''); setNotice('');
    try {
      await adminApi.revokeSession(s.id);
      setNotice('Signed out. Takes full effect when that device\u2019s session next renews (within 8 hours).');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div>
      <h1 className="page-title">My Account</h1>
      <p className="page-subtitle">Your sign-in identity and connected devices.</p>

      <div className="section-head">Account Information</div>
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
          <div>
            <div style={LABEL}>Name</div>
            <div style={VALUE}>{adminUser?.name || '—'}</div>
          </div>
          <div>
            <div style={LABEL}>Email</div>
            <div style={VALUE}>{adminUser?.email || '—'}</div>
          </div>
          <div>
            <div style={LABEL}>Role</div>
            <div style={{ ...VALUE, textTransform: 'capitalize' }}>{adminUser?.role || '—'}</div>
          </div>
        </div>
      </div>

      <div className="section-head">Signed-in Devices</div>
      <div className="card">
        <p style={{ fontSize: 13, color: '#666', fontFamily: 'Arial, sans-serif', marginTop: 0 }}>
          Devices and browsers with a remembered sign-in to your account. Web sessions appear
          here only when &ldquo;Stay logged in&rdquo; was checked. Signing a device out revokes its
          remembered sign-in; it takes full effect when that session next renews (within 8 hours).
        </p>

        {error && <p style={{ color: '#B85042', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>{error}</p>}
        {notice && <p style={{ color: '#2e7d32', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>{notice}</p>}

        {sessions === null && (
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading&hellip;</p>
        )}

        {sessions !== null && sessions.length === 0 && !error && (
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', marginBottom: 0 }}>
            No remembered sessions. (Your current sign-in appears here only if you checked
            &ldquo;Stay logged in&rdquo;, or once the mobile app is connected.)
          </p>
        )}

        {sessions !== null && sessions.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '8px 6px' }}>Type</th>
                <th style={{ padding: '8px 6px' }}>Device</th>
                <th style={{ padding: '8px 6px' }}>Signed in</th>
                <th style={{ padding: '8px 6px' }}>Last active</th>
                <th style={{ padding: '8px 6px' }}>Expires</th>
                <th style={{ padding: '8px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 6px' }}>{kindBadge(s.kind, s.devicePlatform)}</td>
                  <td style={{ padding: '8px 6px' }}>{s.kind === 'mobile' ? (s.deviceName || 'Mobile device') : 'Browser'}</td>
                  <td style={{ padding: '8px 6px' }}>{fmtDate(s.createdAt)}</td>
                  <td style={{ padding: '8px 6px' }}>{fmtDate(s.lastUsedAt || s.createdAt)}</td>
                  <td style={{ padding: '8px 6px' }}>{fmtDate(s.expiresAt)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleRevoke(s)}
                      disabled={revoking === s.id}
                      style={{
                        background: 'none', border: '1px solid #B85042', color: '#B85042',
                        borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                        opacity: revoking === s.id ? 0.5 : 1,
                      }}
                    >
                      {revoking === s.id ? 'Signing out…' : 'Sign out'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
